import { useEffect, useMemo, useRef } from "react";
import { ReferenceManyField, useGetOne, useListContext } from "react-admin";
import { Chip, LinearProgress, Typography } from "@mui/material";
import { z } from "zod";
import { PracticalExamWorkflow } from "../../model/PracticalExamWorkflow";
import { Row } from "../../misc/Row";
import { useSetRecoilState } from "recoil";
import { WorkflowDialog, workflowShownInWorkflowDialogState } from "../../misc/WorkflowDialog";
import { DateTime } from "luxon";
import { formatCalendarWeek, formatDate } from "../../utils/calendar";
import { StudentAvatar } from "../../misc/StudentAvatar";
import { StudentDisplayName } from "../../misc/StudentDisplayName";
import { Student } from "../../providers/studentsProvider";
import { ListCheckIcon } from "../../icons/ListCheckIcon";
import { renderAddress } from "../../misc/AddressDisplay";
import useResizeObserver from "use-resize-observer";
import Board from "../../misc/Board";
import { Column } from "../../misc/Column";
import { InstructorDisplayName } from "../../misc/InstructorDisplayName";

export function DrivingSchoolPracticalExamWorkflowsBoard() {
  return (
    <ReferenceManyField
      reference="workflows"
      target="drivingSchoolUid"
      filter={{ type: "practicalExamSignUp" satisfies PracticalExamWorkflow["type"], finished: false }}
      perPage={9999}
    >
      <_DrivingSchoolPracticalExamWorkflowsBoard />
    </ReferenceManyField>
  );
}

const StageEnum = z.enum([
  "Eignung prüfen",
  "Prüfungstermin organisieren",
  "Warte auf Bestätigung vom Terminvorschlag",
  "Ausbildungsnachweis erstellen",
  "Bereit",
]);
type Stage = z.infer<typeof StageEnum>;

type CardData = PracticalExamWorkflow & { _date: DateTime };

type BoardData = Record<Stage, Array<CardData>>;

function _DrivingSchoolPracticalExamWorkflowsBoard() {
  const { data: workflows, isLoading } = useListContext<PracticalExamWorkflow>();
  const setWorkflowShownInDialog = useSetRecoilState(workflowShownInWorkflowDialogState);
  const autoOpenWorkflowDialogRef = useRef(true);
  useEffect(() => {
    if (isLoading || !workflows || !autoOpenWorkflowDialogRef.current) {
      return;
    }
    autoOpenWorkflowDialogRef.current = false;
    const openDialog = new URL(location.href).searchParams.get("openDialog");
    if (openDialog && openDialog.startsWith("WorkflowDialog!")) {
      const workflowId = openDialog.substring("WorkflowDialog!".length);
      const workflow = workflows.find((it) => it.id === workflowId);
      if (workflow) {
        setWorkflowShownInDialog(workflow);
      }
    }
  }, [isLoading, workflows]);
  const boardData = useMemo(() => {
    const boardData = Object.fromEntries(
      StageEnum.options.map((stage) => [stage, [] as Array<PracticalExamWorkflow>]),
    ) as BoardData;
    if (!workflows) {
      return boardData;
    }
    const addCardToStage = (stage: Stage, workflow: PracticalExamWorkflow) => {
      const card: CardData = {
        ...workflow,
        _date: DateTime.fromFormat(workflow.workflowData.preferredDates[0], "yyyy-MM-dd"),
      };
      const cards = boardData[stage];
      const { _date } = card;
      // Insert card int the correct position (cards should be sorted by calendar week) ...
      const i = cards.findIndex((it) => it._date > _date);
      if (i >= 0) {
        cards.splice(i, 0, card);
      } else {
        cards.push(card);
      }
    };
    for (const workflow of workflows) {
      if (_numDoneTasks(workflow) === _numTasks(workflow)) {
        addCardToStage("Bereit", workflow);
      } else if (workflow.tasks["time_approved_by_student"]?.isDone) {
        addCardToStage("Ausbildungsnachweis erstellen", workflow);
      } else if (workflow.tasks["time_proposal_sent"]?.isDone) {
        addCardToStage("Warte auf Bestätigung vom Terminvorschlag", workflow);
      } else if (workflow.tasks["student_approved_for_exam"]?.isDone) {
        addCardToStage("Prüfungstermin organisieren", workflow);
      } else {
        addCardToStage("Eignung prüfen", workflow);
      }
    }
    return boardData;
  }, [workflows]);
  const { ref: columnHeaderRef, height: columnHeaderHeight } = useResizeObserver({ box: "border-box" });

  return (
    <Board title="Praktische Prüfungen" isLoading={isLoading} variant="mobileScrollable">
      {StageEnum.options.map((stage) => (
        <Board.Column
          key={stage}
          title={stage}
          // Give each column header the same height. Use the height of the column with the longest title as reference ...
          headerHeight={stage === "Warte auf Bestätigung vom Terminvorschlag" ? undefined : columnHeaderHeight}
          headerRef={stage === "Warte auf Bestätigung vom Terminvorschlag" ? columnHeaderRef : undefined}
        >
          {boardData[stage].map((workflow) => (
            <_Card key={workflow.id} workflow={workflow} />
          ))}
        </Board.Column>
      ))}
      <WorkflowDialog />
    </Board>
  );
}

function _Card({ workflow }: { workflow: CardData }) {
  const setWorkflowShownInDialog = useSetRecoilState(workflowShownInWorkflowDialogState);
  const numDoneTasks = _numDoneTasks(workflow);
  const numTasks = _numTasks(workflow);
  const { data: student } = useGetOne<Student>("students", { id: workflow.workflowData.studentUid });
  const bookedTraining = student?.bookedTrainings.find((it) => it.id === workflow.workflowData.bookedTrainingId);

  const examDate = useMemo(() => {
    const practicalExams = bookedTraining?.kpis?.practicalExams;
    if (!practicalExams) {
      return undefined;
    }
    return Object.values(practicalExams).sort((a, b) => b.date.diff(a.date, "days").days)[0]?.date;
  }, [bookedTraining]);

  return (
    <Board.Card onClick={() => setWorkflowShownInDialog(workflow)}>
      <Row sx={{ mb: "8px" }} spacing="7px">
        <StudentAvatar studentId={workflow.workflowData.studentUid} size="20px" />
        <Column sx={{ flex: 1, minWidth: 0 }}>
          <Row sx={{ alignItems: "flex-start", justifyContent: "space-between", gap: "7px" }}>
            <StudentDisplayName
              studentId={workflow.workflowData.studentUid}
              sx={{ fontWeight: 700, minWidth: 0, overflowWrap: "anywhere" }}
            />
            {bookedTraining && (
              <Chip
                label={bookedTraining.drivingLicenseClass}
                size="small"
                sx={{ height: "20px", fontSize: "12px", flexShrink: 0 }}
              />
            )}
          </Row>
          <InstructorDisplayName instructorId={workflow.workflowData.assignedInstructorUid} sx={{ fontSize: "12px" }} />
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px" }}>
            {workflow.workflowData.examLocation.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px" }}>
            {renderAddress(workflow.workflowData.examLocation.postalAddress, { oneLine: true })}
          </Typography>
          {examDate && (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px" }}>
              {formatDate(examDate, true)}
            </Typography>
          )}
        </Column>
      </Row>
      <LinearProgress
        variant="determinate"
        value={(100 * numDoneTasks) / numTasks}
        color={"progress" as any}
        sx={{
          height: "6px",
          borderRadius: "999px",
          ...(numDoneTasks === 0 && { backgroundColor: "grey.200" }),
          "& .MuiLinearProgress-bar": { borderRadius: "999px" },
        }}
      />
      <Row sx={{ mt: "8px" }}>
        <ListCheckIcon sx={{ mt: "-1px", color: "text.secondary" }} />
        <Typography component="p" sx={{ m: 0, ml: "5px", fontSize: "12px", color: "text.secondary" }}>
          {`${numDoneTasks} / ${numTasks}`}
        </Typography>
        <div style={{ flex: 1 }} />
        <Typography component="p" sx={{ m: 0, fontSize: "12px", color: "text.secondary" }}>
          {formatCalendarWeek(workflow._date)}
        </Typography>
      </Row>
    </Board.Card>
  );
}

function _numDoneTasks(workflow: PracticalExamWorkflow): number {
  return Object.values(workflow.tasks).filter((task) => task.isDone).length;
}

function _numTasks(workflow: PracticalExamWorkflow): number {
  return Object.keys(workflow.tasks).length;
}
